import { type BadgeVariant } from '@/components/ui/Badge';
import { cn } from '@/utils/cn';
import { toIsoDate } from '@/api/reportDateRanges';
import type {
  AdminReportCoverageCounts,
  AdminReportCoverageHole,
  AdminReportCoverageQueryCounts,
  AdminReportCoverageWindowCounts,
  ReportCoverageEnqueueResult,
  ReportCoverageParams,
  ReportCoverageStage,
} from '@/api/adminApi';

const EMPTY_COUNTS: AdminReportCoverageCounts = {
  expected: 0,
  ok: 0,
  not_scheduled: 0,
  scheduled: 0,
  failed: 0,
  in_s3_not_db: 0,
  missing_s3: 0,
  missing_db: 0,
};

export const STAGE_LABEL: Record<ReportCoverageStage, string> = {
  not_scheduled: 'Not scheduled',
  scheduled: 'Scheduled',
  failed: 'Failed',
  in_s3_not_db: 'In S3, not DB',
  in_db: 'In DB',
};

export const STAGE_BADGE: Record<ReportCoverageStage, BadgeVariant> = {
  not_scheduled: 'warning',
  scheduled: 'brand',
  failed: 'danger',
  in_s3_not_db: 'archived',
  in_db: 'success',
};

export const S3_SAVED_WINDOWS = ['1h', '2h', '4h', '6h', '12h', '24h'] as const;

export type CoveragePresetId = 'lookback' | 'freshness' | 'custom';

export function queryLabel(queryId: string, labels?: Record<string, string>) {
  return labels?.[queryId] || queryId.split('_').join(' ');
}

export function windowLabel(start: string, end: string) {
  return start === end ? start : `${start} → ${end}`;
}

export function coverageRequestParams(
  presetId: CoveragePresetId,
  startDate: string,
  endDate: string
): ReportCoverageParams {
  if (presetId === 'custom') {
    return { startDate, endDate };
  }
  return { range: presetId };
}

export function enqueueToastMessage(result: ReportCoverageEnqueueResult) {
  const extra = result.skipped
    ? ` Skipped ${result.skipped}${
        result.blocked_payload.length ? ` (${result.blocked_payload.join(', ')})` : ''
      }.`
    : '';
  return `Enqueued ${result.enqueued} report${result.enqueued === 1 ? '' : 's'}.${extra}`;
}

export function isHealableHole(hole: AdminReportCoverageHole) {
  if (hole.stage === 'not_scheduled' || hole.stage === 'failed') return true;
  return hole.stage === 'scheduled' && hole.status === 'queued' && !hole.amazon_report_id;
}

export function stageCount(counts: AdminReportCoverageCounts, stage: ReportCoverageStage): number {
  return stage === 'in_db' ? counts.ok : counts[stage];
}

export function rowCountsForQuery(
  row: AdminReportCoverageCounts & { by_query?: Record<string, AdminReportCoverageQueryCounts> },
  queryFilter: string
): AdminReportCoverageCounts {
  if (queryFilter === 'all') return row;
  return row.by_query?.[queryFilter] ?? EMPTY_COUNTS;
}

export function accountMatchesCoverageFilters(
  row: AdminReportCoverageCounts & { by_query?: Record<string, AdminReportCoverageQueryCounts> },
  stageFilter: string,
  queryFilter: string
): boolean {
  const counts = rowCountsForQuery(row, queryFilter);
  if (queryFilter !== 'all' && !row.by_query?.[queryFilter]) return false;
  if (stageFilter === 'all') return true;
  return stageCount(counts, stageFilter as ReportCoverageStage) > 0;
}

export function holeMatchesCoverageFilters(
  hole: AdminReportCoverageHole,
  stageFilter: string,
  queryFilter: string
): boolean {
  if (stageFilter !== 'all' && hole.stage !== stageFilter) return false;
  if (queryFilter !== 'all' && hole.query_id !== queryFilter) return false;
  return true;
}

export function CoverageListFilters({
  stageFilter,
  queryFilter,
  queryIds,
  queryLabels,
  onStageChange,
  onQueryChange,
  resultLabel,
}: {
  stageFilter: string;
  queryFilter: string;
  queryIds: string[];
  queryLabels: Record<string, string>;
  onStageChange: (value: string) => void;
  onQueryChange: (value: string) => void;
  resultLabel?: string;
}) {
  return (
    <div className="flex flex-wrap gap-3 mb-3">
      <label className="text-xs text-foreground-muted">
        Stage
        <select
          value={stageFilter}
          onChange={(e) => onStageChange(e.target.value)}
          className="ml-2 rounded-lg border border-border bg-surface px-2 py-1 text-sm text-foreground"
        >
          <option value="all">All</option>
          {(Object.keys(STAGE_LABEL) as ReportCoverageStage[]).map((stage) => (
            <option key={stage} value={stage}>
              {STAGE_LABEL[stage]}
            </option>
          ))}
        </select>
      </label>
      <label className="text-xs text-foreground-muted">
        Report
        <select
          value={queryFilter}
          onChange={(e) => onQueryChange(e.target.value)}
          className="ml-2 rounded-lg border border-border bg-surface px-2 py-1 text-sm text-foreground"
        >
          <option value="all">All</option>
          {queryIds.map((queryId) => (
            <option key={queryId} value={queryId}>
              {queryLabel(queryId, queryLabels)}
            </option>
          ))}
        </select>
      </label>
      {resultLabel ? (
        <p className="text-xs text-foreground-subtle self-end">{resultLabel}</p>
      ) : null}
    </div>
  );
}

export function adsCoveragePickerBounds() {
  const today = new Date();
  const toDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 2, 12);
  const fromDate = new Date(today.getFullYear(), today.getMonth() - 12, 1, 12);
  return { fromDate, toDate };
}

export function defaultAdsLookbackRange() {
  const { toDate } = adsCoveragePickerBounds();
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth() - 3, 1, 12);
  return { startDate: toIsoDate(start), endDate: toIsoDate(toDate) };
}

export function defaultAdsThisMonthRange() {
  const { toDate } = adsCoveragePickerBounds();
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1, 12);
  const startDate = toIsoDate(start);
  const endDate = toIsoDate(toDate);
  if (endDate < startDate) {
    return { startDate: endDate, endDate };
  }
  return { startDate, endDate };
}

export function clampRange(
  startDate: string,
  endDate: string,
  bounds: { fromDate: Date; toDate: Date }
) {
  const min = toIsoDate(bounds.fromDate);
  const max = toIsoDate(bounds.toDate);
  let start = startDate < min ? min : startDate;
  let end = endDate > max ? max : endDate;
  if (start > end) start = end;
  return { startDate: start, endDate: end };
}

function monthStart(today: Date, monthsBack: number) {
  return new Date(today.getFullYear(), today.getMonth() - monthsBack, 1, 12);
}

function openMonthRange(monthsBack: number) {
  const today = new Date();
  const toDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 2, 12);
  const startDate = toIsoDate(monthStart(today, monthsBack));
  const endDate = toIsoDate(toDate);
  if (endDate < startDate) {
    return { startDate: endDate, endDate, toDate, fromDate: toDate };
  }
  return { startDate, endDate, toDate, fromDate: monthStart(today, monthsBack) };
}

export function vendorCoveragePickerBounds() {
  const { fromDate, toDate } = openMonthRange(2);
  return { fromDate, toDate };
}

export function defaultVendorLookbackRange() {
  const { startDate, endDate } = openMonthRange(2);
  return { startDate, endDate };
}

export function defaultVendorFreshnessRange() {
  const { startDate, endDate } = openMonthRange(0);
  return { startDate, endDate };
}

export function sellerCoveragePickerBounds() {
  const { fromDate, toDate } = openMonthRange(3);
  return { fromDate, toDate };
}

export function defaultSellerLookbackRange() {
  const { startDate, endDate } = openMonthRange(3);
  return { startDate, endDate };
}

export function defaultSellerFreshnessRange() {
  const { startDate, endDate } = openMonthRange(0);
  return { startDate, endDate };
}

export function CoverageStatCards({ totals }: { totals?: AdminReportCoverageCounts & { accounts?: number; accounts_with_holes?: number } }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
      <div className="rounded-xl border border-border bg-surface px-4 py-3">
        <p className="text-xs text-foreground-muted">Accounts with holes</p>
        <p className="text-headline-md font-semibold tabular-nums">
          {totals?.accounts_with_holes ?? 0}
          <span className="text-sm font-normal text-foreground-subtle"> / {totals?.accounts ?? 0}</span>
        </p>
      </div>
      <div className="rounded-xl border border-border bg-surface px-4 py-3">
        <p className="text-xs text-foreground-muted">Missing scheduled</p>
        <p className="text-headline-md font-semibold tabular-nums">
          {(totals?.not_scheduled ?? 0) + (totals?.scheduled ?? 0)}
        </p>
      </div>
      <div className="rounded-xl border border-border bg-surface px-4 py-3">
        <p className="text-xs text-foreground-muted">Missing S3</p>
        <p className="text-headline-md font-semibold tabular-nums">{totals?.missing_s3 ?? 0}</p>
      </div>
      <div className="rounded-xl border border-border bg-surface px-4 py-3">
        <p className="text-xs text-foreground-muted">Missing DB</p>
        <p className="text-headline-md font-semibold tabular-nums">{totals?.missing_db ?? 0}</p>
      </div>
    </div>
  );
}

export function CoverageRollingHourCards({
  s3Saved,
  failed,
}: {
  s3Saved?: AdminReportCoverageWindowCounts;
  failed?: AdminReportCoverageWindowCounts;
}) {
  return (
    <div className="mb-6">
      <p className="text-xs text-foreground-subtle mb-3">
        Rolling last-N-hours (UTC), not the calendar span above
      </p>
      <div className="mb-2">
        <p className="text-sm font-medium text-foreground">Saved to S3</p>
      </div>
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        {S3_SAVED_WINDOWS.map((window) => (
          <div key={`s3-${window}`} className="rounded-xl border border-border bg-surface px-4 py-3">
            <p className="text-xs text-foreground-muted">{window}</p>
            <p className="text-headline-md font-semibold tabular-nums">{s3Saved?.[window] ?? 0}</p>
          </div>
        ))}
      </div>
      <div className="mb-2">
        <p className="text-sm font-medium text-foreground">Failed</p>
      </div>
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
        {S3_SAVED_WINDOWS.map((window) => {
          const count = failed?.[window] ?? 0;
          return (
            <div key={`failed-${window}`} className="rounded-xl border border-border bg-surface px-4 py-3">
              <p className="text-xs text-foreground-muted">{window}</p>
              <p
                className={cn(
                  'text-headline-md font-semibold tabular-nums',
                  count > 0 ? 'text-error' : undefined
                )}
              >
                {count}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
